/**
 * Integrations Fixture — mirrors Core-Shape exakt.
 * Quelle: lmm-wiki/wiki/orbit-specs/admin-integrations-and-settings-shapes.md
 *
 * Core liefert für GET /v1/admin/integrations:
 *   { items: [{ name, displayName, description, scope, centralOnly,
 *               configSchema: [{key,label,type,required,secret,placeholder?}],
 *               hasEnvFallback, source: null|'tenant'|'env',
 *               preview: null | {<fieldKey>: <maskedOrPlain>},
 *               notes: null|string }],
 *     providers: [...] }
 *
 * Provider-Schemas (Stand A2): smtp, lexoffice, paypal, stripe, captcha, bzst.
 */

const ITEMS_TEMPLATE = [
  {
    name: 'smtp',
    displayName: 'SMTP (Rechnungen, Auth-Mails)',
    description: 'Outbound Mail-Server pro Brand. Default-Fallback aus env wenn nicht gesetzt.',
    scope: 'per-tenant',
    centralOnly: false,
    hasEnvFallback: true,
    configSchema: [
      { key: 'host', label: 'Host', type: 'string', required: true, secret: false, placeholder: 'smtp.brand.com' },
      { key: 'port', label: 'Port', type: 'number', required: true, secret: false, placeholder: '587' },
      { key: 'secure', label: 'TLS', type: 'boolean', required: false, secret: false },
      { key: 'user', label: 'Username', type: 'string', required: true, secret: false },
      { key: 'password', label: 'Passwort', type: 'string', required: true, secret: true },
      { key: 'fromAddress', label: 'From-Adresse', type: 'string', required: true, secret: false, placeholder: 'noreply@brand.com' },
      { key: 'fromName', label: 'From-Name', type: 'string', required: false, secret: false, placeholder: 'Brand' },
    ],
  },
  {
    name: 'lexoffice',
    displayName: 'lexoffice (Buchhaltung)',
    description: 'API-Token für Credit-Notes + optional Outbound-Rechnungen.',
    scope: 'per-tenant',
    centralOnly: false,
    hasEnvFallback: false,
    configSchema: [
      { key: 'apiKey', label: 'API-Token', type: 'string', required: true, secret: true },
      { key: 'baseUrl', label: 'Base-URL', type: 'url', required: false, secret: false, placeholder: 'https://api.lexoffice.io' },
    ],
  },
  {
    name: 'paypal',
    displayName: 'PayPal',
    description: 'Brand-spezifische PayPal-Credentials. Sandbox + Live umschaltbar.',
    scope: 'per-tenant',
    centralOnly: false,
    hasEnvFallback: false,
    configSchema: [
      { key: 'mode', label: 'Modus', type: 'enum:sandbox,live', required: true, secret: false },
      { key: 'clientId', label: 'Client-ID', type: 'string', required: true, secret: false },
      { key: 'clientSecret', label: 'Client-Secret', type: 'string', required: true, secret: true },
      { key: 'webhookId', label: 'Webhook-ID', type: 'string', required: true, secret: false },
      { key: 'returnUrl', label: 'Return-URL', type: 'url', required: true, secret: false },
      { key: 'cancelUrl', label: 'Cancel-URL', type: 'url', required: true, secret: false },
    ],
  },
  {
    name: 'stripe',
    displayName: 'Stripe (Brand-eigener Account)',
    description: 'Nur konfigurieren, wenn diese Brand einen eigenen Stripe-Account betreibt. Plattform-Default-Keys liegen in der Server-Env und sind hier nicht editierbar.',
    scope: 'per-tenant',
    centralOnly: false,
    hasEnvFallback: false,
    configSchema: [
      { key: 'secretKey', label: 'Secret-Key', type: 'string', required: true, secret: true, placeholder: 'sk_live_…' },
      { key: 'publishableKey', label: 'Publishable-Key', type: 'string', required: true, secret: false, placeholder: 'pk_live_…' },
      { key: 'webhookSecret', label: 'Webhook-Signing-Secret', type: 'string', required: false, secret: true, placeholder: 'whsec_…' },
    ],
  },
  {
    name: 'captcha',
    displayName: 'Captcha',
    description: 'Captcha-Provider für § 312q Cancel-Intent-Form.',
    scope: 'per-tenant',
    centralOnly: false,
    hasEnvFallback: true,
    configSchema: [
      { key: 'provider', label: 'Provider', type: 'enum:none,friendly,trustcaptcha,mcaptcha', required: true, secret: false },
      { key: 'siteKey', label: 'Site-Key', type: 'string', required: true, secret: false },
      { key: 'secretKey', label: 'Secret-Key', type: 'string', required: true, secret: true },
      { key: 'baseUrl', label: 'Base-URL (nur mCaptcha)', type: 'url', required: false, secret: false },
    ],
  },
  {
    name: 'bzst',
    displayName: 'BZSt (VAT-ID-Verify)',
    description: 'eVATR-Anfrage-Identität für Live-VAT-ID-Verifikation.',
    scope: 'per-tenant',
    centralOnly: false,
    hasEnvFallback: true,
    configSchema: [
      { key: 'requesterUstId', label: 'Requester-VAT-ID', type: 'string', required: true, secret: false, placeholder: 'DE123456789' },
    ],
  },
];

function persist() {
  if (typeof window === 'undefined') return null;
  if (!window.__ORBIT_INTEGRATIONS_DB__) {
    window.__ORBIT_INTEGRATIONS_DB__ = {};
  }
  return window.__ORBIT_INTEGRATIONS_DB__;
}

function maskPreview(item, config) {
  if (!config) return null;
  const out = {};
  for (const field of item.configSchema) {
    const v = config[field.key];
    if (v == null || v === '') continue;
    if (field.secret) {
      const s = String(v);
      out[field.key] = `••••${s.slice(-4)}`;
    } else {
      out[field.key] = v;
    }
  }
  return Object.keys(out).length ? out : null;
}

export function integrationsFixture() {
  const db = persist();
  const items = ITEMS_TEMPLATE.map((tmpl) => {
    const row = db?.[tmpl.name];
    const source = row ? 'tenant' : (tmpl.hasEnvFallback ? 'env' : null);
    const preview = row ? maskPreview(tmpl, row.config) : null;
    return {
      ...tmpl,
      source,
      preview,
      notes: row?.notes ?? null,
    };
  });
  return { items, providers: ITEMS_TEMPLATE.map((t) => ({ name: t.name })) };
}

export function saveIntegrationFixture(provider, body) {
  const db = persist();
  if (!db) return { ok: true };
  const prev = db[provider] ?? { config: {} };
  const tmpl = ITEMS_TEMPLATE.find((t) => t.name === provider);
  const nextConfig = { ...prev.config };
  if (tmpl && body?.config) {
    for (const field of tmpl.configSchema) {
      const v = body.config[field.key];
      // Per Spec A3: empty string / null on secret means "preserve"
      if (field.secret && (v == null || v === '')) continue;
      // Non-secret: empty string is intentional clear
      nextConfig[field.key] = v;
    }
  }
  db[provider] = { config: nextConfig, notes: body?.notes ?? null };
  return {
    ok: true,
    source: 'tenant',
    preview: tmpl ? maskPreview(tmpl, nextConfig) : null,
    notes: db[provider].notes,
  };
}

export function clearIntegrationFixture(provider) {
  const db = persist();
  if (db) delete db[provider];
  return { ok: true };
}
